import React, { useRef, useState } from "react";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { ASSET_TYPES } from "./VisualProgressTracker";
import {
  parseKmlFile,
  deriveAssetGeometry,
  assetFieldsFromGeometry,
} from "./kmlGeometry";

/**
 * KmzRouteImporter — parses .kmz / .kml files and writes the route coordinates
 * to a progressAsset document as `routeCoords: [{lat, lng, alt?}, ...]`.
 *
 * Behaviour
 * ---------
 *  • .kml — read as text, parsed directly with DOMParser
 *  • .kmz — unzipped via JSZip (loaded lazily from CDN if not bundled),
 *           then the contained doc.kml is parsed
 *  • Supports multiple LineStrings — they are concatenated into one route
 *  • Supports Points — added as discrete markers (saved as routeMarkers)
 *  • Computes total route length using the haversine formula and writes
 *    it back to the asset for verification against the chainage range
 *
 * Props
 * -----
 *  • projectId — Firestore project id
 *  • asset     — the progressAsset doc to attach the route to
 *  • onDone    — called after a successful import
 *  • onClose   — closes the panel
 */
export default function KmzRouteImporter({ projectId, asset, onDone, onClose }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [step, setStep] = useState("idle"); // idle | parsing | review | saving | done | error
  const [error, setError] = useState("");
  const [parsedRoute, setParsedRoute] = useState(null); // {coords, markers, polygons, …}
  const [geometry, setGeometry] = useState(null);       // what this asset type takes from it

  const typeMeta = ASSET_TYPES[asset?.assetType || "pipeline"] || ASSET_TYPES.pipeline;
  const isFootprintAsset = (asset?.assetType || "") === "building";

  /* ---------- File handlers ---------- */

  function acceptFile(f) {
    const name = (f?.name || "").toLowerCase();
    if (!name.endsWith(".kml") && !name.endsWith(".kmz")) {
      setError("Please choose a .kml or .kmz file.");
      return;
    }
    setFile(f);
    setError("");
    setStep("idle");
    setParsedRoute(null);
    setGeometry(null);
  }

  function handleFileChange(e) {
    const f = e.target.files?.[0];
    if (f) acceptFile(f);
  }

  function handleDrop(e) {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) acceptFile(f);
  }

  /* ---------- Parse ---------- */

  async function handleParse() {
    if (!file) return;
    setError("");
    setStep("parsing");

    try {
      const parsed = await parseKmlFile(file);
      const derived = deriveAssetGeometry(parsed, asset?.assetType, {
        measurement: typeMeta.measurement,
      });
      if (!derived.ok) throw new Error(derived.error);

      setParsedRoute(parsed);
      setGeometry(derived);
      setStep("review");
    } catch (err) {
      console.error("KMZ parse failed", err);
      setError(err.message || "Failed to parse the file.");
      setStep("error");
    }
  }

  /* ---------- Save ---------- */

  async function handleSave() {
    if (!geometry?.ok || !asset?.id) return;
    setStep("saving");

    try {
      // Replacing the KMZ re-derives the asset's extent from it — the file is
      // what defines how long a chainage asset is, so the end chainage moves
      // with the surveyed route rather than drifting away from it.
      const fields = assetFieldsFromGeometry(geometry, {
        measurement: typeMeta.measurement,
        startValue: asset.startChainage,
        fileName: file.name,
      });
      await updateDoc(doc(db, "projects", projectId, "progressAssets", asset.id), {
        ...fields,
        routeUpdatedAt: serverTimestamp(),
      });
      setStep("done");
      onDone?.();
    } catch (err) {
      console.error("Route save failed", err);
      setError(err.message || "Failed to save route to the asset.");
      setStep("error");
    }
  }

  /* ---------- Render ---------- */

  const fileSizeKb = file ? (file.size / 1024).toFixed(0) : "";

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>
              {isFootprintAsset ? "🏗️ Import Footprint from KMZ/KML" : "🗺️ Import Route from KMZ/KML"}
            </h2>
            <p style={styles.subtitle}>
              Attaching to: <strong>{asset?.assetName || "—"}</strong>
            </p>
          </div>
          <button style={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div style={styles.body}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".kml,.kmz"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />

          {/* Drop zone */}
          <div
            onClick={() => step !== "parsing" && step !== "saving" && fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            style={{
              ...styles.dropzone,
              borderColor: isDragging ? "#0ea5e9" : file ? "#10b981" : "#cbd5e1",
              background: isDragging ? "#f0f9ff" : file ? "#ecfdf5" : "#f8fafc",
            }}
          >
            <div style={styles.dropIcon}>{file ? "📍" : "📂"}</div>
            {file ? (
              <>
                <p style={styles.dropTitle}>{file.name}</p>
                <p style={styles.dropSub}>{fileSizeKb} KB · click to change</p>
              </>
            ) : (
              <>
                <p style={styles.dropTitle}>Drop a .kmz or .kml file here</p>
                <p style={styles.dropSub}>or click to browse</p>
              </>
            )}
          </div>

          {/* Helper info */}
          <div style={styles.info}>
            {isFootprintAsset ? (
              <>
                <strong>How to get a footprint KMZ:</strong> in Google Earth Pro, use the Polygon
                tool to click the <em>four corners of the building</em>, then right-click the
                polygon → <em>Save Place As</em> → <em>KMZ</em>. The outer boundary becomes the
                footprint, and its perimeter and plan area are measured from it.
              </>
            ) : (
              <>
                <strong>How to get a KMZ:</strong> in Google Earth Pro, draw your pipeline as a Path,
                right-click it → <em>Save Place As</em> → <em>KMZ</em>. The first LineString in the
                file becomes the route; multiple LineStrings will be concatenated. Any standalone
                Points are imported as markers (e.g. valves, manholes, tie-ins). The asset's length
                is measured from the route.
              </>
            )}
          </div>

          {error && <div style={styles.error}>{error}</div>}

          {/* Parse button */}
          {file && step === "idle" && (
            <button style={styles.primaryButton} onClick={handleParse}>
              📐 Parse {file.name.toLowerCase().endsWith(".kmz") ? "KMZ" : "KML"}
            </button>
          )}

          {step === "parsing" && (
            <div style={styles.statusRow}>
              <Spinner /> Parsing file and extracting coordinates…
            </div>
          )}

          {/* Review */}
          {step === "review" && geometry && (
            <RoutePreview parsed={parsedRoute} geometry={geometry} asset={asset} onSave={handleSave} />
          )}

          {step === "saving" && (
            <div style={styles.statusRow}>
              <Spinner /> Saving route to asset…
            </div>
          )}

          {step === "done" && (
            <div style={styles.success}>
              ✓ Saved to <strong>{asset.assetName}</strong>.{" "}
              {geometry.kind === "footprint"
                ? `${geometry.corners.length}-corner footprint · ${Math.round(geometry.areaM2).toLocaleString("en-ZA")} m² · ${Math.round(geometry.perimeterM).toLocaleString("en-ZA")} m perimeter.`
                : `${geometry.coords.length} coordinates · ${Math.round(geometry.lengthM).toLocaleString("en-ZA")} m${typeMeta.measurement === "chainage" ? " — the chainage range now follows the route." : "."}`}
              <button style={{ ...styles.primaryButton, marginTop: 12 }} onClick={onClose}>Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Route preview block                                                 */
/* ------------------------------------------------------------------ */

function RoutePreview({ parsed, geometry, asset, onSave }) {
  const isFootprint = geometry.kind === "footprint";
  const start = Number(asset?.startChainage ?? 0);
  const newEnd = start + Math.round(geometry.lengthM);
  const currentEnd = Number(asset?.endChainage ?? 0);
  const chainageMoves = !isFootprint && currentEnd > 0 && newEnd !== currentEnd;

  return (
    <div style={styles.review}>
      <div style={styles.reviewHeader}>
        <h3 style={{ margin: 0, fontSize: 15, color: "#0f172a" }}>
          {isFootprint ? "Footprint Preview" : "Route Preview"}
        </h3>
        <span style={styles.badge}>{parsed.placemarks.length} placemark{parsed.placemarks.length === 1 ? "" : "s"}</span>
      </div>

      <div style={styles.reviewGrid}>
        {isFootprint ? (
          <>
            <Metric
              label="Corners"
              value={geometry.corners.length}
              warning={!!geometry.warning}
              warningText={geometry.warning}
            />
            <Metric label="Plan Area" value={`${Math.round(geometry.areaM2).toLocaleString("en-ZA")} m²`} />
            <Metric label="Perimeter" value={`${Math.round(geometry.perimeterM).toLocaleString("en-ZA")} m`} />
            <Metric label="Markers" value={geometry.markers.length.toLocaleString("en-ZA")} />
          </>
        ) : (
          <>
            <Metric label="Coordinates" value={geometry.coords.length.toLocaleString("en-ZA")} />
            <Metric label="Markers" value={geometry.markers.length.toLocaleString("en-ZA")} />
            <Metric label="Route Length" value={`${Math.round(geometry.lengthM).toLocaleString("en-ZA")} m`} />
            <Metric
              label="New Chainage Range"
              value={`CH ${start.toLocaleString("en-ZA")} → CH ${newEnd.toLocaleString("en-ZA")}`}
              warning={chainageMoves}
              warningText={chainageMoves ? `replaces CH ${start.toLocaleString("en-ZA")} → CH ${currentEnd.toLocaleString("en-ZA")}` : ""}
            />
          </>
        )}
      </div>

      {/* Mini SVG preview */}
      <RouteSvg coords={geometry.coords} markers={geometry.markers} />

      <div style={styles.coordTable}>
        <div style={styles.coordTableHeader}>
          <span>#</span><span>Latitude</span><span>Longitude</span><span>Altitude</span>
        </div>
        {geometry.coords.slice(0, 8).map((c, i) => (
          <div key={i} style={styles.coordRow}>
            <span>{i + 1}</span>
            <span>{c.lat.toFixed(6)}</span>
            <span>{c.lng.toFixed(6)}</span>
            <span>{c.alt != null ? `${c.alt.toFixed(1)} m` : "—"}</span>
          </div>
        ))}
        {geometry.coords.length > 8 && (
          <div style={styles.coordFooter}>… and {geometry.coords.length - 8} more</div>
        )}
      </div>

      <button style={styles.primaryButton} onClick={onSave}>
        {isFootprint ? "✓ Save Footprint to Asset" : "✓ Save Route to Asset"}
      </button>
    </div>
  );
}

function Metric({ label, value, warning, warningText }) {
  return (
    <div style={{
      ...styles.metric,
      background: warning ? "#fef3c7" : "#f8fafc",
      borderColor: warning ? "#f59e0b" : "#e5e7eb",
    }}>
      <span style={styles.metricLabel}>{label}</span>
      <strong style={styles.metricValue}>{value}</strong>
      {warningText && <span style={styles.metricWarning}>{warningText}</span>}
    </div>
  );
}

/* ----- Mini SVG preview of the route ----- */

function RouteSvg({ coords, markers }) {
  if (!coords?.length) return null;

  const lats = coords.map((c) => c.lat);
  const lngs = coords.map((c) => c.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const padLat = Math.max((maxLat - minLat) * 0.1, 0.0005);
  const padLng = Math.max((maxLng - minLng) * 0.1, 0.0005);

  const project = (lat, lng) => {
    const x = ((lng - (minLng - padLng)) / ((maxLng + padLng) - (minLng - padLng))) * 600;
    const y = 220 - ((lat - (minLat - padLat)) / ((maxLat + padLat) - (minLat - padLat))) * 220;
    return [x, y];
  };

  const pathD = coords
    .map((c, i) => {
      const [x, y] = project(c.lat, c.lng);
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <div style={styles.svgWrap}>
      <svg viewBox="0 0 600 220" style={{ width: "100%", height: "100%", display: "block" }}>
        <defs>
          <linearGradient id="kmzbg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#dbeafe" />
            <stop offset="100%" stopColor="#e2e8f0" />
          </linearGradient>
        </defs>
        <rect width="600" height="220" fill="url(#kmzbg)" />
        {/* Grid */}
        {Array.from({ length: 6 }).map((_, i) => (
          <line key={`gx-${i}`} x1={i * 100} y1="0" x2={i * 100} y2="220" stroke="#cbd5e1" strokeWidth="0.5" />
        ))}
        {Array.from({ length: 4 }).map((_, i) => (
          <line key={`gy-${i}`} x1="0" y1={i * 55} x2="600" y2={i * 55} stroke="#cbd5e1" strokeWidth="0.5" />
        ))}

        <path d={pathD} fill="none" stroke="#0ea5e9" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d={pathD} fill="none" stroke="#ffffff" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />

        {/* Start / end markers */}
        {coords[0] && (() => {
          const [x, y] = project(coords[0].lat, coords[0].lng);
          return <circle cx={x} cy={y} r="6" fill="#22c55e" stroke="#ffffff" strokeWidth="2" />;
        })()}
        {coords[coords.length - 1] && (() => {
          const [x, y] = project(coords[coords.length - 1].lat, coords[coords.length - 1].lng);
          return <circle cx={x} cy={y} r="6" fill="#ef4444" stroke="#ffffff" strokeWidth="2" />;
        })()}

        {/* Markers */}
        {markers.map((m, i) => {
          const [x, y] = project(m.lat, m.lng);
          return (
            <g key={i}>
              <circle cx={x} cy={y} r="4" fill="#f59e0b" stroke="#ffffff" strokeWidth="1.5" />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* KML parser                                                          */
/* ------------------------------------------------------------------ */




/* ------------------------------------------------------------------ */
/* Lazy-load JSZip from CDN if not bundled                             */
/* ------------------------------------------------------------------ */


/* ------------------------------------------------------------------ */
/* Spinner                                                              */
/* ------------------------------------------------------------------ */

function Spinner() {
  return (
    <>
      <style>{`@keyframes kmzspin { to { transform: rotate(360deg); } }`}</style>
      <span style={{
        display: "inline-block",
        width: 18, height: 18,
        border: "2px solid #cbd5e1",
        borderTopColor: "#0ea5e9",
        borderRadius: "50%",
        animation: "kmzspin 0.7s linear infinite",
        marginRight: 10,
        verticalAlign: "middle",
      }} />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const styles = {
  backdrop: {
    position: "fixed", inset: 0,
    background: "rgba(15,23,42,0.72)", backdropFilter: "blur(6px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1100, padding: 24,
  },
  modal: {
    width: "100%", maxWidth: 760, maxHeight: "92vh",
    background: "#ffffff", borderRadius: 20,
    boxShadow: "0 40px 100px rgba(0,0,0,0.45)",
    display: "grid", gridTemplateRows: "auto 1fr",
    overflow: "hidden", border: "1px solid #cbd5e1",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "16px 22px",
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
    color: "#ffffff",
  },
  title: { margin: 0, fontSize: 18, color: "#ffffff" },
  subtitle: { margin: "4px 0 0", fontSize: 12, color: "#cbd5e1" },
  closeButton: {
    border: "none", background: "rgba(255,255,255,0.15)",
    color: "#ffffff", fontSize: 22, lineHeight: 1,
    width: 34, height: 34, borderRadius: 10, cursor: "pointer",
  },
  body: {
    padding: 20,
    display: "grid", gap: 14,
    overflowY: "auto",
  },

  dropzone: {
    border: "2px dashed",
    borderRadius: 14, padding: "26px 20px", textAlign: "center",
    cursor: "pointer", transition: "all 0.15s",
  },
  dropIcon: { fontSize: 36, marginBottom: 8 },
  dropTitle: { margin: 0, fontWeight: 700, fontSize: 14, color: "#0f172a" },
  dropSub: { margin: "4px 0 0", fontSize: 12, color: "#64748b" },

  info: {
    padding: "10px 14px",
    background: "#f0f9ff",
    border: "1px solid #bae6fd",
    borderRadius: 10,
    fontSize: 12,
    color: "#0c4a6e",
    lineHeight: 1.5,
  },
  error: {
    padding: "10px 14px",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 10,
    color: "#991b1b",
    fontSize: 13,
    fontWeight: 600,
  },
  success: {
    padding: "14px 18px",
    background: "#ecfdf5",
    border: "1px solid #6ee7b7",
    borderRadius: 12,
    color: "#065f46",
    fontSize: 14,
    fontWeight: 600,
    textAlign: "center",
  },

  primaryButton: {
    border: "none", borderRadius: 12, padding: "12px 18px",
    background: "#0f172a", color: "#ffffff", fontWeight: 700,
    fontSize: 14, cursor: "pointer",
  },

  statusRow: { display: "flex", alignItems: "center", color: "#475569", fontSize: 13, fontWeight: 600 },

  review: { display: "grid", gap: 14 },
  reviewHeader: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  reviewGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 },

  metric: {
    border: "1px solid",
    borderRadius: 10, padding: "10px 12px",
    display: "grid", gap: 2,
  },
  metricLabel: { fontSize: 11, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 },
  metricValue: { fontSize: 16, color: "#0f172a" },
  metricWarning: { fontSize: 11, color: "#92400e", fontWeight: 700, marginTop: 2 },

  badge: {
    background: "#eef2ff", color: "#3730a3",
    borderRadius: 999, padding: "3px 10px",
    fontSize: 11, fontWeight: 700,
  },

  svgWrap: {
    width: "100%", height: 220,
    background: "#ffffff",
    border: "1px solid #e5e7eb", borderRadius: 12,
    overflow: "hidden",
  },

  coordTable: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    fontSize: 12,
  },
  coordTableHeader: {
    display: "grid", gridTemplateColumns: "40px 1fr 1fr 80px",
    background: "#f1f5f9", padding: "8px 12px", fontWeight: 700, color: "#0f172a",
  },
  coordRow: {
    display: "grid", gridTemplateColumns: "40px 1fr 1fr 80px",
    padding: "6px 12px",
    borderTop: "1px solid #e5e7eb",
    color: "#334155",
    fontFamily: "ui-monospace, SFMono-Regular, monospace",
  },
  coordFooter: {
    padding: "8px 12px",
    borderTop: "1px solid #e5e7eb",
    color: "#64748b",
    fontStyle: "italic",
    fontSize: 12,
    textAlign: "center",
  },
};
